import { useRef, useCallback, useEffect, useState } from 'react'
import ForceGraph3D from 'react-force-graph-3d'
import ForceGraph2D from 'react-force-graph-2d'
import * as THREE from 'three'
import SpriteText from 'three-spritetext'
import { Zap } from 'lucide-react'
import type { GraphData, GraphNode } from '../types'
import { ENTITY_COLORS } from '../lib/graph-utils'
import { useBrainSimulation } from '../hooks/useBrainSimulation'
import BrainControlPanel from './BrainControlPanel'

const NEON_GREEN = '#39ff14'
const GHOST_OPACITY = 0.04
const GHOST_LABEL_OPACITY = 0.04
const GHOST_LINK_OPACITY = 0.03

interface GraphViewProps {
  data: GraphData
  selectedNode: GraphNode | null
  highlightedNodes: Set<string>
  onNodeClick: (node: GraphNode) => void
  onBackgroundClick: () => void
  is3D: boolean
  brainMode: boolean
  onBrainModeToggle: (active: boolean) => void
}

export default function GraphView({
  data,
  selectedNode,
  highlightedNodes,
  onNodeClick,
  onBackgroundClick,
  is3D,
  brainMode,
  onBrainModeToggle,
}: GraphViewProps) {
  const fgRef = useRef<any>(undefined)
  const containerRef = useRef<HTMLDivElement>(null)
  const [dimensions, setDimensions] = useState({ width: 0, height: 0 })
  const [showHint, setShowHint] = useState(true)
  const lastTapRef = useRef<number>(0)
  const touchStartRef = useRef<{ x: number; y: number; time: number } | null>(null)

  // Brain mode local state
  const [isRunning, setIsRunning] = useState(false)
  const [edgeTimeMs, setEdgeTimeMs] = useState(80)   // ms per edge crossing
  const [fadeMs, setFadeMs] = useState(1200)          // ms for glow trail
  const [startNodeId, setStartNodeId] = useState<string | undefined>()
  const [displayLitCount, setDisplayLitCount] = useState(0)
  const [fps, setFps] = useState(0)
  const [memMB, setMemMB] = useState(0)

  const { stateRef, nodeCount, reset } = useBrainSimulation({
    data,
    edgeTimeMs,
    fadeMs,
    isRunning: brainMode && isRunning,
    startNodeId,
  })

  // Stored refs for direct Three.js mutation
  const nodeMeshRefs = useRef<Map<string, {
    mesh: THREE.Mesh
    glow: THREE.Mesh
    label: SpriteText | null
  }>>(new Map())

  // Link material refs for direct mutation (linkColor is cached by library, doesn't update per-frame)
  const linkMaterialRefs = useRef<Map<string, THREE.MeshBasicMaterial>>(new Map())

  // Reset brain state when toggled off
  useEffect(() => {
    if (!brainMode) {
      setIsRunning(false)
      setStartNodeId(undefined)
      reset()
      nodeMeshRefs.current.clear()
      linkMaterialRefs.current.clear()
      setDisplayLitCount(0)
    }
  }, [brainMode, reset])

  // Refresh objects ONCE when brain mode toggles
  const prevBrainMode = useRef(brainMode)
  useEffect(() => {
    if (prevBrainMode.current !== brainMode && fgRef.current) {
      setTimeout(() => fgRef.current?.refresh(), 50)
    }
    prevBrainMode.current = brainMode
  }, [brainMode])

  // === BRAIN ANIMATION LOOP — setTimeout at 240fps target, not vsync-locked ===
  useEffect(() => {
    if (!brainMode || !is3D) return

    const TARGET_FPS = 240
    const FRAME_MS = 1000 / TARGET_FPS
    let timerId: ReturnType<typeof setTimeout>
    let frameCount = 0
    let lastFpsTime = performance.now()

    const animate = () => {
      timerId = setTimeout(animate, FRAME_MS)
      frameCount++

      const now = performance.now()
      if (now - lastFpsTime >= 1000) {
        setFps(Math.round(frameCount * 1000 / (now - lastFpsTime)))
        frameCount = 0
        lastFpsTime = now
        const perf = performance as any
        if (perf.memory) {
          setMemMB(Math.round(perf.memory.usedJSHeapSize / 1048576))
        }
      }

      const s = stateRef.current

      for (const [nodeId, r] of nodeMeshRefs.current) {
        const brightness = s.nodes.get(nodeId) || 0
        const mat = r.mesh.material as THREE.MeshLambertMaterial
        mat.opacity = GHOST_OPACITY + brightness * (1.0 - GHOST_OPACITY)
        const glowMat = r.glow.material as THREE.MeshBasicMaterial
        glowMat.opacity = brightness * 0.45
        if (r.label) {
          r.label.material.opacity = GHOST_LABEL_OPACITY + brightness * (0.7 - GHOST_LABEL_OPACITY)
        }
      }

      for (const [linkKey, mat] of linkMaterialRefs.current) {
        const brightness = s.links.get(linkKey) || 0
        mat.opacity = GHOST_LINK_OPACITY + brightness * (0.85 - GHOST_LINK_OPACITY)
      }
    }

    timerId = setTimeout(animate, FRAME_MS)
    return () => clearTimeout(timerId)
  }, [brainMode, is3D, stateRef])

  // Slow display update for control panel
  useEffect(() => {
    if (!brainMode || !isRunning) return
    const interval = setInterval(() => {
      setDisplayLitCount(stateRef.current.nodes.size)
    }, 200)
    return () => clearInterval(interval)
  }, [brainMode, isRunning, stateRef])

  // Helper to get link key
  const getLinkKey = useCallback((link: any) => {
    const srcId = typeof link.source === 'object' ? link.source.id : link.source
    const tgtId = typeof link.target === 'object' ? link.target.id : link.target
    return `${srcId}|${tgtId}|${link.relationship}`
  }, [])

  // Handle resize
  useEffect(() => {
    const updateDimensions = () => {
      if (containerRef.current) {
        setDimensions({
          width: containerRef.current.clientWidth,
          height: containerRef.current.clientHeight,
        })
      }
    }
    updateDimensions()
    window.addEventListener('resize', updateDimensions)
    return () => window.removeEventListener('resize', updateDimensions)
  }, [])

  // Configure d3 forces
  useEffect(() => {
    if (fgRef.current) {
      fgRef.current.d3Force('link')?.distance(80)
      fgRef.current.d3Force('charge')?.strength(-80)
      fgRef.current.d3Force('center')?.strength(1)
    }
  }, [data, is3D])

  useEffect(() => {
    const timer = setTimeout(() => setShowHint(false), 5000)
    return () => clearTimeout(timer)
  }, [])

  const handleDoubleTap = useCallback(() => {
    if (!is3D || !fgRef.current) return
    const camera = fgRef.current.camera()
    const currentPos = camera.position
    const distance = Math.sqrt(currentPos.x ** 2 + currentPos.y ** 2 + currentPos.z ** 2)
    const scale = (distance * 0.6) / distance
    fgRef.current.cameraPosition(
      { x: currentPos.x * scale, y: currentPos.y * scale, z: currentPos.z * scale },
      { x: 0, y: 0, z: 0 }, 500
    )
  }, [is3D])

  const handleTouchStart = useCallback((e: React.TouchEvent) => {
    if (e.touches.length === 1) {
      touchStartRef.current = { x: e.touches[0].clientX, y: e.touches[0].clientY, time: Date.now() }
    }
    if (showHint) setShowHint(false)
  }, [showHint])

  const handleTouchEnd = useCallback((e: React.TouchEvent) => {
    if (e.changedTouches.length !== 1 || !touchStartRef.current) return
    const touch = e.changedTouches[0]
    const dx = Math.abs(touch.clientX - touchStartRef.current.x)
    const dy = Math.abs(touch.clientY - touchStartRef.current.y)
    const dt = Date.now() - touchStartRef.current.time
    if (dx < 10 && dy < 10 && dt < 300) {
      const now = Date.now()
      const timeSinceLastTap = now - lastTapRef.current
      if (timeSinceLastTap < 300 && timeSinceLastTap > 0) {
        handleDoubleTap()
        lastTapRef.current = 0
      } else {
        lastTapRef.current = now
      }
    }
    touchStartRef.current = null
  }, [handleDoubleTap])

  const handleResetView = useCallback(() => {
    if (!fgRef.current) return
    if (is3D) {
      fgRef.current.cameraPosition({ x: 0, y: 0, z: 300 }, { x: 0, y: 0, z: 0 }, 1000)
    } else {
      fgRef.current.centerAt(0, 0, 1000)
      fgRef.current.zoom(1, 1000)
    }
  }, [is3D])

  useEffect(() => {
    if (!selectedNode || brainMode) return
    const focusOnNode = () => {
      if (!fgRef.current) return false
      const node = data.nodes.find(n => n.id === selectedNode.id)
      if (node && node.x !== undefined) {
        if (is3D) {
          fgRef.current.cameraPosition(
            { x: node.x, y: node.y, z: (node.z || 0) + 100 },
            { x: node.x, y: node.y, z: node.z || 0 }, 1000
          )
        } else {
          fgRef.current.centerAt(node.x, node.y, 1000)
          fgRef.current.zoom(3, 1000)
        }
        return true
      }
      return false
    }
    if (focusOnNode()) return
    let attempts = 0
    const interval = setInterval(() => {
      attempts++
      if (focusOnNode() || attempts > 30) clearInterval(interval)
    }, 200)
    return () => clearInterval(interval)
  }, [selectedNode, data.nodes, is3D, brainMode])

  const handleNodeClick = useCallback(
    (node: any) => {
      if (brainMode) {
        if (!isRunning) setStartNodeId(node.id)
        return
      }
      const graphNode = data.nodes.find(n => n.id === node.id)
      if (graphNode) onNodeClick(graphNode)
    },
    [data.nodes, onNodeClick, brainMode, isRunning]
  )

  // 3D node objects — stores refs for direct material mutation
  const nodeThreeObject = useCallback(
    (node: any) => {
      const isSelected = selectedNode?.id === node.id
      const isHighlighted = highlightedNodes.has(node.id)
      const baseSize = (node.size || 4) * 1.8
      const scale = isSelected ? 1.6 : isHighlighted ? 1.4 : 1
      const size = baseSize
      const isBrain = brainMode
      const nodeColor = isBrain
        ? NEON_GREEN
        : (node.color || ENTITY_COLORS[node.entityType as keyof typeof ENTITY_COLORS] || '#ffffff')

      const group = new THREE.Group()
      const segments = isBrain ? 10 : 16
      const geometry = new THREE.SphereGeometry(size * scale, segments, segments)
      const material = new THREE.MeshLambertMaterial({
        color: nodeColor,
        transparent: true,
        opacity: isBrain ? GHOST_OPACITY : (isSelected || isHighlighted ? 1 : 0.8),
      })
      const mesh = new THREE.Mesh(geometry, material)

      const glowGeometry = new THREE.SphereGeometry(size * scale * 1.5, segments, segments)
      const glowMaterial = new THREE.MeshBasicMaterial({
        color: nodeColor,
        transparent: true,
        opacity: isBrain ? 0 : (isSelected || isHighlighted ? 0.2 : 0),
      })
      const glowMesh = new THREE.Mesh(glowGeometry, glowMaterial)
      mesh.add(glowMesh)
      group.add(mesh)

      const labelText = (node.label || '').length > 20
        ? (node.label || '').slice(0, 18) + '...' : (node.label || '')
      let sprite: SpriteText | null = null
      if (labelText) {
        sprite = new SpriteText(labelText)
        sprite.color = isBrain ? NEON_GREEN : nodeColor
        sprite.textHeight = 8
        sprite.material.depthWrite = false
        sprite.material.opacity = isBrain ? GHOST_LABEL_OPACITY : 0.65
        sprite.material.transparent = true
        sprite.position.y = size * scale + 8
        group.add(sprite)
      }

      if (isBrain) {
        nodeMeshRefs.current.set(node.id, { mesh, glow: glowMesh, label: sprite })
      }

      return group
    },
    [selectedNode, highlightedNodes, brainMode]
  )

  // 2D node painting (brain mode is 3D only)
  const nodeCanvasObject = useCallback(
    (node: any, ctx: CanvasRenderingContext2D) => {
      const isSelected = selectedNode?.id === node.id
      const isHighlighted = highlightedNodes.has(node.id)
      const baseSize = (node.size || 4) * 1.2
      const scale = isSelected ? 1.6 : isHighlighted ? 1.4 : 1
      const radius = baseSize * scale
      const color = node.color || ENTITY_COLORS[node.entityType as keyof typeof ENTITY_COLORS] || '#ffffff'
      if (isSelected || isHighlighted) {
        ctx.beginPath()
        ctx.arc(node.x, node.y, radius * 1.6, 0, 2 * Math.PI)
        ctx.fillStyle = color + '33'
        ctx.fill()
      }
      ctx.beginPath()
      ctx.arc(node.x, node.y, radius, 0, 2 * Math.PI)
      ctx.fillStyle = color
      ctx.globalAlpha = isSelected || isHighlighted ? 1 : 0.8
      ctx.fill()
      ctx.globalAlpha = 1
      const label = (node.label || '').length > 20
        ? (node.label || '').slice(0, 18) + '...' : (node.label || '')
      if (label) {
        ctx.font = '4px sans-serif'
        ctx.textAlign = 'center'
        ctx.textBaseline = 'bottom'
        ctx.fillStyle = color
        ctx.globalAlpha = 0.65
        ctx.fillText(label, node.x, node.y - radius - 2)
        ctx.globalAlpha = 1
      }
    },
    [selectedNode, highlightedNodes]
  )

  // linkMaterial: in brain mode, create neon green materials and store refs for animation
  // This OVERRIDES linkColor for 3D — the animation loop mutates opacity directly
  const linkMaterialAccessor = useCallback((link: any) => {
    if (!brainMode) return undefined as any // falsy → use default + linkColor
    const key = getLinkKey(link)
    const mat = new THREE.MeshBasicMaterial({
      color: NEON_GREEN,
      transparent: true,
      opacity: GHOST_LINK_OPACITY,
      depthWrite: false,
    })
    linkMaterialRefs.current.set(key, mat)
    return mat
  }, [brainMode, getLinkKey])

  // Link color — used only in non-brain mode (brain mode uses linkMaterial)
  const linkColor = useCallback((link: any) => {
    return link.color || 'rgba(255,255,255,0.25)'
  }, [])

  // Link width
  const linkWidthAccessor = useCallback((_link: any) => {
    return brainMode ? 1.5 : 1.5
  }, [brainMode])

  // No particles in brain mode (link glow is handled by material mutation)
  const linkParticlesAccessor = useCallback((_link: any) => {
    return brainMode ? 0 : 2
  }, [brainMode])

  const linkParticleColor = useCallback((link: any) => {
    return link.color || '#ffffff'
  }, [])

  if (dimensions.width === 0) {
    return <div ref={containerRef} className="w-full h-full bg-black" />
  }

  const sharedProps = {
    ref: fgRef,
    graphData: data,
    width: dimensions.width,
    height: dimensions.height,
    backgroundColor: '#000000' as const,
    linkWidth: linkWidthAccessor,
    linkOpacity: 0.6,
    linkColor: linkColor,
    linkDirectionalParticles: linkParticlesAccessor,
    linkDirectionalParticleWidth: 2,
    linkDirectionalParticleColor: linkParticleColor,
    onNodeClick: handleNodeClick,
    onBackgroundClick: onBackgroundClick,
    enableNodeDrag: !brainMode,
    enableNavigationControls: true,
    showNavInfo: false,
    d3AlphaDecay: 0.01,
    d3VelocityDecay: 0.4,
    d3AlphaMin: 0.001,
    warmupTicks: 100,
    cooldownTicks: 300,
  }

  return (
    <div
      ref={containerRef}
      className="w-full h-full bg-black relative"
      onTouchStart={handleTouchStart}
      onTouchEnd={handleTouchEnd}
    >
      {is3D ? (
        <ForceGraph3D
          {...sharedProps}
          nodeThreeObject={nodeThreeObject}
          nodeThreeObjectExtend={false}
          controlType="orbit"
          linkMaterial={linkMaterialAccessor}
        />
      ) : (
        <ForceGraph2D
          {...sharedProps}
          nodeCanvasObject={nodeCanvasObject}
          nodeCanvasObjectMode={() => 'replace' as const}
        />
      )}

      {showHint && !brainMode && (
        <div className="absolute top-4 left-1/2 -translate-x-1/2 bg-black/70 backdrop-blur-sm px-4 py-2 rounded-full text-xs text-gray-400 border border-gray-800 pointer-events-none animate-pulse">
          <span>Pinch to zoom - Drag to rotate - Double-tap to zoom in</span>
        </div>
      )}

      <div className="absolute top-4 right-4 flex gap-2">
        {is3D && (
          <button
            onClick={() => onBrainModeToggle(!brainMode)}
            className={`bg-black/70 backdrop-blur-sm p-2 rounded-full border transition-colors ${
              brainMode
                ? 'text-yellow-400 border-yellow-500/50 hover:border-yellow-400'
                : 'text-gray-400 hover:text-white border-gray-800 hover:border-gray-600'
            }`}
            title={brainMode ? 'Exit brain mode' : 'Neural activity simulation'}
          >
            <Zap size={20} />
          </button>
        )}
        <button
          onClick={handleResetView}
          className="bg-black/70 backdrop-blur-sm p-2 rounded-full text-gray-400 hover:text-white border border-gray-800 hover:border-gray-600 transition-colors"
          title="Reset view"
        >
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M3 12a9 9 0 0 1 9-9 9.75 9.75 0 0 1 6.74 2.74L21 8" />
            <path d="M21 3v5h-5" />
            <path d="M21 12a9 9 0 0 1-9 9 9.75 9.75 0 0 1-6.74-2.74L3 16" />
            <path d="M3 21v-5h5" />
          </svg>
        </button>
      </div>

      {brainMode && !isRunning && stateRef.current.nodes.size === 0 && (
        <div className="absolute top-4 left-1/2 -translate-x-1/2 bg-black/70 backdrop-blur-sm px-4 py-2 rounded-full text-xs text-gray-400 border border-gray-800 pointer-events-none">
          {startNodeId
            ? <span>Start node selected. Press <span className="text-white">Start</span> to begin.</span>
            : <span>Click a node to select start point, or press <span className="text-white">Start</span> for default.</span>
          }
        </div>
      )}

      {brainMode && (
        <BrainControlPanel
          isRunning={isRunning}
          onToggleRun={() => setIsRunning(r => !r)}
          edgeTimeMs={edgeTimeMs}
          onEdgeTimeChange={setEdgeTimeMs}
          fadeMs={fadeMs}
          onFadeChange={setFadeMs}
          onRestart={() => {
            setIsRunning(false)
            setStartNodeId(undefined)
            reset()
            setDisplayLitCount(0)
            linkMaterialRefs.current.clear()
            if (fgRef.current) setTimeout(() => fgRef.current?.refresh(), 50)
          }}
          onClose={() => onBrainModeToggle(false)}
          nodeCount={nodeCount}
          litNodeCount={displayLitCount}
          fps={fps}
          memMB={memMB}
        />
      )}
    </div>
  )
}
